import { useState } from "react";
import { Zap } from "lucide-react";
import {
  useCreate,
  useGetList,
  useNotify,
  useRecordContext,
  useRefresh,
  useTranslate,
} from "ra-core";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";

import { crmErrorMessage } from "../misc/crmErrorMessage";
import type { Contact, PurchaseSignalType } from "../types";

// [hiperautomacao] One click for the seller to tell the system what the
// person did ("asked for a quote with measurements"). The database turns
// it into a new temperature, a call task and the end of nurturing.
export const RegisterSignalButton = () => {
  const record = useRecordContext<Contact>();
  const translate = useTranslate();
  const [isOpen, setIsOpen] = useState(false);
  if (!record) return null;

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="h-7 cursor-pointer"
        onClick={() => setIsOpen(true)}
      >
        <Zap className="w-4 h-4" />
        {translate("hiper.temperature.signal.action")}
      </Button>
      {isOpen ? (
        <RegisterSignalDialog
          contactId={record.id}
          onClose={() => setIsOpen(false)}
        />
      ) : null}
    </>
  );
};

interface RegisterSignalDialogProps {
  contactId: Contact["id"];
  onClose: () => void;
}

const RegisterSignalDialog = ({
  contactId,
  onClose,
}: RegisterSignalDialogProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [create, { isPending }] = useCreate();
  const [type, setType] = useState("");
  const [note, setNote] = useState("");
  const { data: types, isPending: isLoadingTypes } =
    useGetList<PurchaseSignalType>("purchase_signal_types", {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "position", order: "ASC" },
    });

  const handleSave = () => {
    if (!type) return;
    create(
      "purchase_signals",
      { data: { contact_id: contactId, type, note: note.trim() || null } },
      {
        onSuccess: () => {
          notify("hiper.temperature.signal.saved", { type: "success" });
          refresh();
          onClose();
        },
        onError: (error) => {
          notify(crmErrorMessage(error), { type: "error" });
        },
      },
    );
  };

  return (
    <Dialog open onOpenChange={(open) => (open ? null : onClose())}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {translate("hiper.temperature.signal.title")}
          </DialogTitle>
          <DialogDescription>
            {translate("hiper.temperature.signal.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label>{translate("hiper.temperature.signal.type")}</Label>
            {!isLoadingTypes && !types?.length ? (
              <p className="text-sm text-muted-foreground">
                {translate("hiper.temperature.signal.empty_types")}
              </p>
            ) : null}
            <RadioGroup
              value={type}
              onValueChange={setType}
              aria-label={translate("hiper.temperature.signal.type")}
              className="gap-2"
            >
              {types?.map((signalType) => (
                <Label
                  key={signalType.id}
                  className="flex items-center gap-3 rounded-md border px-3 py-2 font-normal cursor-pointer has-[[data-state=checked]]:border-primary"
                >
                  <RadioGroupItem value={String(signalType.id)} />
                  <span className="flex-1">{signalType.label}</span>
                  <span className="text-xs text-muted-foreground">
                    {translate(
                      `hiper.temperature.signal.strength.${signalType.strength}`,
                    )}
                  </span>
                </Label>
              ))}
            </RadioGroup>
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="purchase-signal-note">
              {translate("hiper.temperature.signal.note")}
            </Label>
            <Textarea
              id="purchase-signal-note"
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {translate("ra.action.cancel")}
          </Button>
          <Button onClick={handleSave} disabled={!type || isPending}>
            {translate("hiper.temperature.signal.action")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
